// ============================================================================
// Game Provider Configuration
// Frontend only configures: icon mapping + auto-generated styling
// All game data (name, status, etc.) comes from backend API: GET /game-providers
// ============================================================================

export interface GameProvider {
  id: string;
  code?: string; // Provider code from API (e.g., "bluedragon")
  name: string;
  tagline: string;
  gradient: string;
  icon: string; // Path to icon image (e.g., "/icons/vegasx.jpg")
  /** Status from server: active = normal, inactive = COMING SOON, maintenance = under maintenance */
  status?: 'active' | 'inactive' | 'maintenance';
  /** Deposit type: 'auto' = instant, 'manual' = staff processes (5min-2hrs) */
  depositType?: 'auto' | 'manual';
  /** Admin badge on card; omit or empty = hidden */
  clientTag?: string | null;
  /** Min deposit per-provider từ config admin (USD); undefined = dùng fallback */
  minDeposit?: number;
}

// ============================================================================
// Icon Mapping
// Maps provider code → icon path. If not found, uses default icon.
// To add a new game icon: add entry here and place icon file in /public/icons/
// ============================================================================

const ICON_MAP: Record<string, string> = {
  'vegasx': '/icons/vegasx.jpg',
  'bluedragon': '/icons/blue dragon.jpg',
  'firekirin': '/icons/fire kirin.jpg',
  'gamevault': '/icons/game vault.jpg',
  'pandamaster': '/icons/panda.png',
  'goldentreasure': '/icons/golden treasure.jpg',
  'goldrush': '/icons/golden treasure.jpg',
  'ultrapanda': '/icons/utrapanda.jpg',
  'vblink777': '/icons/vblink.jpg',
  'vblink': '/icons/vblink.jpg',
  'egame99': '/icons/egame.jpg',
  'egame': '/icons/egame.jpg',
  'sweeps': '/icons/sweeps.png',
  'lasvegassweeps': '/icons/sweeps.png',
  'cashier': '/icons/vegasx.jpg',
  'royalspin': '/icons/sweeps.png',
  'aceplay': '/icons/egame.jpg',
  'riverpay': '/icons/riverpay.jpg',
  'cashfrenzy': '/icons/cashfrenzy.png',
  'gameroom': '/icons/gameroom777.png',
  'mrallinone': '/icons/mrallinone.png',
  'mafia': '/icons/mafia.jpeg',
  'mafia77777': '/icons/mafia.jpeg',
  'vegasroll': '/icons/vegasroll.png',
  'mwterminal': '/icons/mkterminal.jpeg',
  'osterminal': '/icons/orionstart.jpeg',
  'openjack2win': '/icons/jack2win.jpeg',
  'galaxyworld': '/icons/galaxyworld.jpg',
  'megaspin': '/icons/megaspin.jpg',
  'vegas7games': '/icons/vegas7games.jpg',
  'glamorspin': '/icons/glamour_spin.jpg',
  'juwa': '/icons/juwa777.jpeg',
  'goldendragon': '/icons/golden_dragon.jpg',
  'noble777': '/icons/noble777.png',
};

export const DEFAULT_PROVIDER_ICON = '/icons/egame.jpg';

/**
 * Gets icon path for a provider code. Returns default icon if not configured.
 */
export function getIconPathForProvider(code: string): string {
  const normalizedCode = code.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
  const path = ICON_MAP[normalizedCode] || DEFAULT_PROVIDER_ICON;
  // Paths may include spaces ("blue dragon.jpg") — encode so <img src> loads reliably.
  return encodeURI(path);
}

/**
 * Whether a provider has a dedicated icon configured (vs. the generic fallback).
 * Used to decide between real tile art and the Cosmos nebula fallback.
 */
export function hasIconForProvider(code: string | undefined | null): boolean {
  if (!code) return false;
  const normalizedCode = code.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
  return normalizedCode in ICON_MAP;
}

/**
 * Build a unique icon list from provider codes and always include fallback icon.
 * Useful for first-load prefetch so offline screen has ready assets.
 */
export function getProviderIconPaths(codes: Array<string | undefined | null>): string[] {
  const unique = new Set<string>();
  codes.forEach((code) => {
    if (!code) return;
    unique.add(getIconPathForProvider(code));
  });
  unique.add(DEFAULT_PROVIDER_ICON);
  return Array.from(unique);
}

// ============================================================================
// Auto-generated styling (gradient, tagline) for providers without explicit config
// ============================================================================

const gradientTemplates = [
  'bg-gradient-to-br from-red-600 via-pink-600 to-purple-700',
  'bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-700',
  'bg-gradient-to-br from-yellow-500 via-orange-600 to-red-600',
  'bg-gradient-to-br from-amber-500 via-yellow-600 to-orange-700',
  'bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700',
  'bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700',
  'bg-gradient-to-br from-pink-500 via-rose-600 to-red-700',
  'bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-700',
  'bg-gradient-to-br from-cyan-500 via-blue-600 to-indigo-700',
  'bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-700',
  'bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-700',
  'bg-gradient-to-br from-orange-500 via-red-600 to-pink-700',
  'bg-gradient-to-br from-lime-500 via-green-600 to-emerald-700',
  'bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-700',
  'bg-gradient-to-br from-rose-500 via-pink-600 to-fuchsia-700',
  'bg-gradient-to-br from-amber-400 via-orange-500 to-red-600',
];

const taglineTemplates = [
  'Premium Gaming Experience',
  'Where Winners Play',
  'Your Ultimate Gaming Destination',
  'Experience the Thrill',
  'Play and Win Big',
  'The Best Gaming Platform',
  'Unleash Your Luck',
  'Gaming Excellence',
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

function getGradientForProvider(code: string, name: string): string {
  const hash = hashString(code.toLowerCase() + name.toLowerCase());
  return gradientTemplates[hash % gradientTemplates.length];
}

function getTaglineForProvider(code: string, name: string): string {
  const hash = hashString(code.toLowerCase() + name.toLowerCase());
  return taglineTemplates[hash % taglineTemplates.length];
}

// ============================================================================
// Map API response → GameProvider component format
// ============================================================================

export interface GameProviderApiItem {
  id: string;
  code: string;
  name: string;
  status: 'active' | 'inactive' | 'maintenance';
  displayOrder?: number;
  clientTag?: string | null;
  minDeposit?: number; // config admin per-provider (BE trả trong GET /game-providers)
  config?: {
    depositType?: 'auto' | 'manual';
  };
}

/**
 * Maps a single API provider item to a GameProvider for UI rendering.
 * Uses icon map for icons, auto-generates gradient and tagline.
 * All data (id, name, status) comes from API.
 */
export const mapApiProviderToComponent = (apiProvider: GameProviderApiItem): GameProvider | null => {
  // Maintenance: never surface on kiosk (V1). Inactive = Coming soon — still listed.
  if (apiProvider.status === 'maintenance') {
    return null;
  }
  const normalizedCode = apiProvider.code.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
  const tagRaw = apiProvider.clientTag;
  const clientTag =
    tagRaw != null && String(tagRaw).trim() !== '' ? String(tagRaw).trim().slice(0, 64) : undefined;

  return {
    id: apiProvider.id,
    code: normalizedCode,
    name: apiProvider.name,
    tagline: getTaglineForProvider(apiProvider.code, apiProvider.name),
    gradient: getGradientForProvider(apiProvider.code, apiProvider.name),
    icon: getIconPathForProvider(apiProvider.code),
    status: apiProvider.status,
    depositType: apiProvider.config?.depositType || 'auto',
    ...(clientTag ? { clientTag } : {}),
    ...(apiProvider.minDeposit != null ? { minDeposit: Number(apiProvider.minDeposit) } : {}),
  };
};

/** True when provider is Coming soon (inactive) — visible but not actionable. */
export function isComingSoonProvider(status?: string | null): boolean {
  return status === 'inactive';
}

/** Home / play surfaces hide maintenance; service already strips them (defense in depth). */
export function excludeMaintenanceProviders<T extends { status?: string | null }>(items: T[]): T[] {
  return items.filter((i) => i.status !== 'maintenance');
}
